/**
 * Beat-grid filler for long empty stretches of imported beatmaps.
 *
 * osu! sources routinely open with a several-second intro (and
 * occasionally a mid-song breakdown) where the mapper charted nothing.
 * Imported verbatim those stretches play as dead air. This module finds
 * the genuinely-long empty gaps and emits a steady metronome pattern to
 * fill them so the player always has *something* to do.
 *
 * The pattern is deliberately plain: notes land ON the beat (integer
 * beats), in a "4 on, 4 off" shape phase-locked to the global 8-beat
 * grid so each burst sits on a phrase downbeat. This is NOT the Pulse
 * `findBurstChambers` / onset-driven approach — there is no audio
 * analysis here, just the chart's own beat grid. Per-mode callers turn
 * the returned beats into their own note shape (circles, mania notes,
 * waves obstacles).
 */

/** Beats to skip at the very start so filler never collides with the count-in / GO overlay. */
export const FILLER_FROM_BEAT = 4;

/**
 * Minimum length of the LEADING gap (count-in → first note) before it is
 * backfilled. Lower than the internal floor because a silent intro is
 * the most jarring kind of dead air, it's always safe to fill (no
 * preceding notes), and it's the case the request explicitly calls out.
 * 8 beats keeps short count-ins (first note ~beat 8-12) untouched while
 * catching the multi-second silent intros osu! sources routinely open
 * with.
 */
export const FILLER_LEADING_MIN_GAP_BEATS = 8;

/**
 * Minimum length of an INTERNAL gap (between two notes) before it is
 * backfilled. Conservative — 16 beats (four bars of 4/4) — so authored
 * breakdowns and musical rests stay as the mapper intended; only a
 * genuinely-dead mid-song stretch gets filler.
 */
export const FILLER_INTERNAL_MIN_GAP_BEATS = 16;

/** Phrase length the on/off pattern locks to. 8 beats = two bars of 4/4. */
export const FILLER_SECTION_BEATS = 8;

/** Beats with a note at the start of each phrase; the rest of the phrase is silent ("4 on, 4 off"). */
export const FILLER_ON_BEATS = 4;

/**
 * Clearance left at each end of a gap so the last filler note isn't glued
 * to the real note that closes the gap (nor the first to the note that
 * opens it). On the LEADING gap the start side is the count-in boundary
 * ({@link FILLER_FROM_BEAT}) rather than a real note, so there the margin
 * is simply extra breathing room after the count-in.
 */
export const FILLER_EDGE_MARGIN_BEATS = 2;

/**
 * Phase of an integer beat within the on/off phrase grid (`0..sectionBeats-1`).
 * A beat carries a filler note when its phase is `< onBeats` — the "4 on,
 * 4 off" shape. The double-mod stays correct for negative beats too
 * (callers only pass non-negative grid beats today, but the helper
 * shouldn't have to care).
 */
export function fillerPhase(beat: number, sectionBeats: number = FILLER_SECTION_BEATS): number {
  return ((beat % sectionBeats) + sectionBeats) % sectionBeats;
}

/**
 * A span of beats occupied by real chart content. Point notes pass
 * `endBeat === startBeat`; held / spanned content (sliders, spinners,
 * mania holds) passes the full span so filler is never placed *inside* a
 * long note that merely looks like a gap when only its start beat is
 * considered.
 */
export interface FillerSpan {
  startBeat: number;
  endBeat: number;
}

export interface ComputeFillerBeatsOpts {
  fromBeat?: number;
  leadingMinGapBeats?: number;
  internalMinGapBeats?: number;
  sectionBeats?: number;
  onBeats?: number;
  edgeMarginBeats?: number;
}

/**
 * Given the occupied spans of a chart, return the integer beats that
 * should carry a filler note. Considers the leading gap (count-in →
 * first note) and any internal gap between consecutive notes; never the
 * trailing gap after the final note (we don't tack notes onto the outro).
 *
 * Returns an empty array when the chart has no notes at all — a
 * note-less chart is a different problem, not "a gap before the notes".
 */
export function computeFillerBeats(
  occupied: ReadonlyArray<FillerSpan>,
  opts: ComputeFillerBeatsOpts = {},
): number[] {
  const fromBeat = opts.fromBeat ?? FILLER_FROM_BEAT;
  const leadingMinGapBeats = opts.leadingMinGapBeats ?? FILLER_LEADING_MIN_GAP_BEATS;
  const internalMinGapBeats = opts.internalMinGapBeats ?? FILLER_INTERNAL_MIN_GAP_BEATS;
  const sectionBeats = opts.sectionBeats ?? FILLER_SECTION_BEATS;
  const onBeats = opts.onBeats ?? FILLER_ON_BEATS;
  const edgeMargin = opts.edgeMarginBeats ?? FILLER_EDGE_MARGIN_BEATS;

  // Merge occupied spans into disjoint coverage intervals, sorted by
  // start. Gaps are the gaps between coverage intervals.
  const spans = occupied
    .filter((s) => Number.isFinite(s.startBeat) && Number.isFinite(s.endBeat))
    .map((s) => ({
      start: Math.min(s.startBeat, s.endBeat),
      end: Math.max(s.startBeat, s.endBeat),
    }))
    .sort((a, b) => a.start - b.start);
  if (spans.length === 0) {
    return [];
  }
  const coverage: Array<{ start: number; end: number }> = [];
  for (const s of spans) {
    const last = coverage[coverage.length - 1];
    if (last && s.start <= last.end) {
      last.end = Math.max(last.end, s.end);
    } else {
      coverage.push({ start: s.start, end: s.end });
    }
  }

  // Leading gap (count-in → first note) + internal gaps. No trailing gap.
  const gaps: Array<{ start: number; end: number }> = [];
  const firstStart = coverage[0].start;
  if (firstStart - fromBeat >= leadingMinGapBeats) {
    gaps.push({ start: fromBeat, end: firstStart });
  }
  for (let i = 0; i < coverage.length - 1; i += 1) {
    const gapStart = coverage[i].end;
    const gapEnd = coverage[i + 1].start;
    if (gapEnd - gapStart >= internalMinGapBeats) {
      gaps.push({ start: gapStart, end: gapEnd });
    }
  }

  const out: number[] = [];
  for (const gap of gaps) {
    const lo = gap.start + edgeMargin;
    const hi = gap.end - edgeMargin;
    for (let beat = Math.ceil(lo); beat <= hi; beat += 1) {
      // Phase-lock the on/off pattern to the global section grid so each
      // burst lands on a phrase downbeat regardless of where the gap
      // happens to begin.
      if (fillerPhase(beat, sectionBeats) < onBeats) {
        out.push(beat);
      }
    }
  }
  return out;
}
